import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where, orderBy, Timestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useEffectiveTenantId } from "../lib/impersonation";
import { api, callableErrorMessage } from "../lib/api";

type StaffMember = {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: string;
};

type PendingInvite = {
  token: string;
  email: string;
  displayName: string | null;
  expiresAt: Date | null;
};

export function StaffTab() {
  const tenantId = useEffectiveTenantId();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [salesByStaff, setSalesByStaff] = useState<Map<string, { count: number; total: number }>>(new Map());

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(
      query(collection(db, "tenants", tenantId, "users"), where("role", "==", "staff")),
      (snap) => {
        setStaff(
          snap.docs.map((d) => ({
            uid: d.id,
            email: d.data().email ?? null,
            displayName: d.data().displayName ?? null,
            role: d.data().role
          }))
        );
      }
    );
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(
      query(collection(db, "tenants", tenantId, "staffInvites"), where("used", "==", false)),
      (snap) => {
        setInvites(
          snap.docs.map((d) => ({
            token: d.id,
            email: d.data().email,
            displayName: d.data().displayName ?? null,
            expiresAt: (d.data().expiresAt as Timestamp | undefined)?.toDate() ?? null
          }))
        );
      }
    );
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    return onSnapshot(
      query(
        collection(db, "tenants", tenantId, "purchases"),
        where("createdAt", ">=", Timestamp.fromDate(startOfDay)),
        where("source", "==", "cashier_assisted"),
        orderBy("createdAt", "desc")
      ),
      (snap) => {
        const map = new Map<string, { count: number; total: number }>();
        snap.docs.forEach((d) => {
          const data = d.data();
          if (data.status === "undone") return;
          const uid = data.cashierUid as string;
          if (!uid) return;
          const prev = map.get(uid) ?? { count: 0, total: 0 };
          map.set(uid, { count: prev.count + 1, total: prev.total + (data.amountFinal ?? 0) });
        });
        setSalesByStaff(map);
      }
    );
  }, [tenantId]);

  return (
    <div className="flex flex-col gap-4">
      <InviteStaffCard tenantId={tenantId} />

      {invites.length > 0 && (
        <section className="card">
          <h3 className="text-sm font-medium text-leaf-700">Invitaciones pendientes</h3>
          <ul className="mt-2 flex flex-col gap-2">
            {invites.map((inv) => (
              <li key={inv.token} className="flex justify-between text-sm">
                <span>{inv.email}</span>
                <span className="text-xs text-leaf-500">
                  {inv.expiresAt ? `Vence ${inv.expiresAt.toLocaleDateString("es-CL")}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card">
        <h3 className="text-sm font-medium text-leaf-700">Cajeros activos ({staff.length})</h3>
        {staff.length === 0 && <p className="mt-2 text-xs text-leaf-500">Aun no hay cajeros.</p>}
        <ul className="mt-2 flex flex-col gap-2">
          {staff.map((s) => {
            const today = salesByStaff.get(s.uid);
            return (
              <li key={s.uid} className="flex items-center justify-between gap-2 border-t border-leaf-100 pt-2 first:border-0 first:pt-0">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-leaf-950">
                    {s.displayName ?? s.email}
                  </p>
                  <p className="text-xs text-leaf-500">
                    {today
                      ? `Hoy: ${today.count} ventas · ${formatCLP(today.total)}`
                      : "Sin ventas hoy"}
                  </p>
                </div>
                <RevokeButton tenantId={tenantId} uid={s.uid} />
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

function InviteStaffCard({ tenantId }: { tenantId: string | null | undefined }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ acceptUrl: string; whatsappUrl: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.inviteStaff({ tenantId, email: email.trim(), displayName: name.trim() || undefined });
      setResult({ acceptUrl: res.data.acceptUrl, whatsappUrl: res.data.whatsappUrl });
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card flex flex-col gap-3">
      <h3 className="text-sm font-medium text-leaf-700">Invitar cajero</h3>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Nombre (opcional)"
        className="input"
      />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="cajero@email.com"
        className="input"
      />
      {error && <p className="text-xs text-red-700">{error}</p>}
      <button onClick={send} disabled={busy || !email.includes("@")} className="btn-primary">
        {busy ? "Generando…" : "Generar invitacion"}
      </button>

      {result && (
        <div className="rounded-2xl bg-leaf-50 p-3 text-xs text-leaf-800">
          <p className="font-medium">Listo. El link va por email automaticamente.</p>
          <a
            href={result.whatsappUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-white"
          >
            Compartir por WhatsApp
          </a>
          <p className="mt-2 break-all font-mono text-[10px] text-leaf-500">{result.acceptUrl}</p>
        </div>
      )}
    </section>
  );
}

function RevokeButton({ tenantId, uid }: { tenantId: string | null | undefined; uid: string }) {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (!tenantId) return;
    if (!confirm("Revocar acceso a este cajero?")) return;
    setBusy(true);
    try {
      await api.revokeStaff({ tenantId, uid });
    } catch (err) {
      alert(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={click} disabled={busy} className="text-xs text-red-700 hover:underline">
      {busy ? "…" : "Revocar"}
    </button>
  );
}

function formatCLP(n: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0
  }).format(n);
}
